import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect, useDispatch } from 'react-redux'

import { setProjectIdentity } from '@store/actionSlices/projectIdentity'
import {
  LocalAssetOptions,
  ProjectIdentity,
  RootStateTypeExtra,
} from '@store/types'

import { TextInput } from '@components/form-controls'
import { Modal } from '@components/modals'
import ToggleSwitch from '@components/toggle-switch'

export interface AssetSettingProp {
  projectIdentity: ProjectIdentity
}

function AssetSettings({ projectIdentity }: AssetSettingProp) {
  const { assetHost, assetPort, assetOptions } = projectIdentity

  const [showModal, toggleModal] = useState(false)

  const dispatch = useDispatch()

  const [port, setPort] = useState('')
  const [portInputError, setPortInputError] = useState('')
  const [hostValue, setHostValue] = useState('')
  const [hostInputError, setHostInputError] = useState('')

  const [assetOptionsValue, setAssetOptionsValue] =
    useState<LocalAssetOptions>(assetOptions)

  const closeModal = () => {
    setPortInputError('')
    setPort(assetPort)
    setHostInputError('')
    setHostValue(assetHost)
    toggleModal(false)
  }

  const isValidPort = (value: number) => {
    const MAXIMUM_VALUE_FOR_IPv4_NETWORK_PORT = 65535
    return value > 0 && value <= MAXIMUM_VALUE_FOR_IPv4_NETWORK_PORT
  }

  const disableAssetOptionsInput = useMemo(() => !port.trim()?.length, [port])

  const handleSave = () => {
    setPortInputError('')
    setHostInputError('')

    if (port && !isValidPort(Number(port))) {
      setPortInputError('The given port is not valid.')
      return
    }

    dispatch(
      setProjectIdentity({
        ...projectIdentity,
        assetHost: hostValue,
        assetPort: port,
        assetOptions: assetOptionsValue,
      })
    )

    toggleModal(false)
  }

  const handleRemovePort = () =>
    dispatch(setProjectIdentity({ ...projectIdentity, assetPort: '' }))

  const handleRemoveHost = () => {
    dispatch(setProjectIdentity({ ...projectIdentity, assetHost: '' }))
  }

  const handleChangeLocalImagesInput = useCallback(() => {
    setAssetOptionsValue((prevVal) => ({
      ...prevVal,
      localImages: !prevVal?.localImages,
    }))
  }, [])

  const handleChangeLocalVideosInput = useCallback(() => {
    setAssetOptionsValue((prevVal) => ({
      ...prevVal,
      localVideos: !prevVal?.localVideos,
    }))
  }, [])

  const handleOnClick = () => {
    toggleModal(true)
  }

  useEffect(() => {
    setPort(assetPort)
  }, [assetPort])

  useEffect(() => {
    setHostValue(assetHost)
  }, [assetHost])

  useEffect(() => {
    setAssetOptionsValue(assetOptions)
  }, [assetOptions])

  return (
    <>
      <button
        type="button"
        className={`${
          projectIdentity.masterKey
            ? 'cursor-not-allowed opacity-60'
            : 'cursor-hand'
        } inline-flex w-full items-center justify-center rounded-lg bg-gradient px-20 py-6 text-subHeading font-medium text-neutralColour shadow-standard`}
        onClick={(e) =>
          projectIdentity.masterKey ? e.preventDefault() : handleOnClick()
        }
      >
        Asset Settings
      </button>

      <Modal
        title=" Asset Settings"
        isVisible={showModal}
        toggleModal={toggleModal}
        handleClose={closeModal}
        handleApply={() => {
          if (hostValue || port) {
            handleSave()
          } else {
            toggleModal(false)
          }
        }}
        applyButtonText="Apply"
      >
        <div className="flex flex-col gap-5">
          <p className="text-center text-default leading-relaxed text-fontBlackColour opacity-60">
            An asset pack hosted on your local machine can be used for improved
            performance.
          </p>
          <div className="flex flex-col gap-2.5">
            <TextInput
              name="assetHost"
              label="Host/IP"
              value={hostValue}
              placeholder="Enter Host/IP"
              className="rounded-lg border-[#D9D9D9]"
              disabled={assetHost !== ''}
              onChange={(event) => setHostValue(event.target.value)}
              onClear={() => {
                if (assetHost) {
                  handleRemoveHost()
                } else {
                  setHostValue('')
                }
              }}
              error={hostInputError}
            />
            <TextInput
              name="port"
              label="Port"
              value={port}
              placeholder="8080"
              disabled={assetPort !== ''}
              onChange={(event) => {
                const numericValue = event.target.value.replace(/\D/g, '')
                setPort(numericValue)
              }}
              onClear={() => {
                if (assetPort) {
                  handleRemovePort()
                } else {
                  setPort('')
                  setPortInputError('')
                }
              }}
              error={portInputError}
            />

            <div
              className={`flex flex-col gap-2.5 ${
                disableAssetOptionsInput ? 'opacity-50' : ''
              }`}
            >
              <div className="flex items-center rounded-lg border border-gray-200 px-2 py-4 text-default">
                <span className="mr-auto">Local Images:</span>
                <ToggleSwitch
                  id="localImagesInput"
                  key="localImagesInput"
                  onChangeCallback={handleChangeLocalImagesInput}
                  initialState={assetOptionsValue?.localImages}
                  isDisabled={disableAssetOptionsInput}
                />
              </div>
              <div className="flex items-center rounded-lg border border-gray-200 px-2 py-4 text-default">
                <span className="mr-auto">Local Videos:</span>
                <ToggleSwitch
                  id="localVideosInput"
                  key="localVideosInput"
                  onChangeCallback={handleChangeLocalVideosInput}
                  initialState={assetOptionsValue?.localVideos}
                  isDisabled={disableAssetOptionsInput}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

const mapStateToProps = ({ projectIdentity }: RootStateTypeExtra) => ({
  projectIdentity,
})

export default connect(mapStateToProps)(AssetSettings)
